import { useTranslation } from "react-i18next";
import { cn } from "@jbnl/shared-ui";
import { buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton, SkeletonRegion } from "@/components/ui/skeleton";
import { MicrosoftMark } from "@/components/brand/ProviderMark";
import { DevSignIn } from "./DevSignIn";
import {
  isKnownProvider,
  providerLabel,
  useProviders,
  type ProviderScheme,
} from "./useProviders";

/** Links, not fetches: the OIDC flow leaves this origin and returns as a top-level navigation. */
export function SignIn() {
  const { t } = useTranslation(["session", "common"]);
  const { data: schemes, isPending } = useProviders();

  const returnUrl = `${window.location.pathname}${window.location.search}`;
  const offered = schemes?.filter((scheme): scheme is ProviderScheme => isKnownProvider(scheme)) ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("signIn.title")}</CardTitle>
        <CardDescription>{t("signIn.description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isPending ? (
          <SkeletonRegion className="space-y-2">
            <Skeleton className="h-12 w-full" />
          </SkeletonRegion>
        ) : offered.length > 0 ? (
          <div className="flex flex-col gap-2">
            {offered.map((scheme) => (
              <a
                key={scheme}
                className={cn(
                  buttonVariants({ variant: "outline" }),
                  "h-12 w-full gap-3 border-2 border-brand-blue text-base font-semibold",
                )}
                href={`/api/auth/challenge/${scheme}?returnUrl=${encodeURIComponent(returnUrl)}`}
                data-testid={`signin-${scheme}`}
              >
                <MicrosoftMark />
                {t("signIn.withProvider", { provider: providerLabel(scheme) })}
              </a>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("signIn.noneConfigured")}</p>
        )}

        <p className="border-t pt-4 text-sm text-muted-foreground">
          {t("signIn.elsewhereBefore")}
          <a className="jb-link" href={`https://${t("common:aanmeldsite")}`}>
            {t("common:aanmeldsite")}
          </a>
          {t("signIn.elsewhereAfter")}
        </p>

        <DevSignIn />
      </CardContent>
    </Card>
  );
}
